import React from 'react';
import ReactHighcharts from 'highcharts-react-official';
import Highcharts from 'highcharts';
import moment from 'moment-timezone';
import { Unit } from '../../helpers/unit';

export default class GraphViewOfficial extends React.Component {
    constructor(props) {
        super(props);
        this.bindGraphData = this.bindGraphData.bind(this);
        this.showLoading = this.showLoading.bind(this);
        this.unit = Unit.getInstance();
        this.chart = null;
        this.chartRef = null;

        Highcharts.setOptions({ global: { useUTC: true } });
        this.colors = {
            titleColor: '#ffffff',
            backgroundColor: '#002651',
            primaryColor: '#0051BD',
            secondaryColor: '#ffffff',
            trendColor: "#FF7F00"
        };

        this.state = {
            chartOptions: {
                credits: {
                    enabled: false
                },
                chart: {
                    height: 250,
                    type: 'area',
                    plotBorderWidth: 0,
                    plotBorderColor: this.colors.primaryColor,
                    backgroundColor: this.colors.backgroundColor,
                    marginLeft: 60,
                    marginBottom: 43,
                },
                title: null,
                plotOptions: {
                    area: {
                        marker: {
                            enabled: false,
                            symbol: 'none',
                            radius: 0,
                            states: {
                                hover: {
                                    enabled: false
                                }
                            }
                        }
                    }
                },
                legend: {
                    enabled: false,
                },
                xAxis: {
                    gridLineWidth: 0.5,
                    gridLineColor: this.colors.primaryColor,
                    lineColor: this.colors.primaryColor,
                    type: 'datetime',
                    tickPositioner: function () {
                        let positions = [],
                            tick = Math.floor(this.dataMin),
                            diff = (this.dataMax - this.dataMin) / (24 * 3600 * 1000),
                            tickSlot = (diff === 1) ? 6 : 4,
                            increment = Math.ceil((this.dataMax - this.dataMin) / tickSlot);

                        if (this.dataMax !== null && this.dataMin !== null) {
                            for (tick; tick - increment <= this.dataMax; tick += increment) {
                                positions.push(tick);
                            }
                        }
                        return positions;
                    },
                    labels: {
                        rotation: -33,
                        style: {
                            fontSize: '0.5rem',
                            color: this.colors.titleColor
                        },
                        format: "{value:%I %p}",
                        formatter: function () {
                            const diff = (this.axis.dataMax - this.axis.dataMin) / (24 * 3600 * 1000);
                            return (diff === 1) ? this.axis.defaultLabelFormatter.call(this) : Highcharts.dateFormat('%e %b %I %p', this.value);
                        }
                    }
                },
                yAxis: {
                    gridLineWidth: 0.5,
                    gridLineColor: this.colors.primaryColor,
                    title: {
                        text: 'Total Acres',
                        style: { color: this.colors.titleColor, margin: 0 }
                    },
                    allowDecimals: false,
                    labels: {
                        rotation: -60,
                        style: {
                            fontSize: '0.55rem',
                            color: this.colors.titleColor,
                        },
                        formatter: function () { return this.value }
                    },
                    min: 0
                },
                tooltip: {
                    enabled: false,
                },
                series: []
            }
        };
    };

    componentDidMount() {
        if (this.props.height) {
            const state = { ...this.state };
            state.chartOptions.chart.height = this.props.height;
            this.setState(state);
        }
    }

    componentWillReceiveProps(nextProps) {
        if (nextProps.graphData && nextProps.graphData !== this.props.graphData) {
            this.bindGraphData(nextProps.graphData);
        }
    }

    bindGraphData(graphData) {
        if (Object.keys(graphData).length > 0 && graphData.isFetching === false) {
            let { chartOptions } = this.state;
            if (chartOptions.series.length > 0 && chartOptions.series[0].length > 0) return;
            let series = [];
            let plannedLine = [];
            let actualLine = [];
            let trendLine = [];

            for (var i = 0; i < graphData.planned.length; i++) {
                const acres = this.unit.toArea(graphData.planned[i][1]);
                plannedLine.push([this.parseUTCTime(graphData.planned[i][0]), acres]);
            };

            for (var j = 0; j < graphData.actual.length; j++) {
                const acres = this.unit.toArea(graphData.actual[j][1]);
                actualLine.push([this.parseUTCTime(graphData.actual[j][0]), acres]);
            };

            for (var k = 0; k < graphData.trend.length; k++) {
                const acres = this.unit.toArea(graphData.trend[k][1]);
                trendLine.push([this.parseUTCTime(graphData.trend[k][0]), acres]);
            };

            series.push({
                fillOpacity: 0.5,
                color: this.colors.primaryColor,
                lineWidth: 1.5,
                marker: {
                    enabled: false
                },
                data: plannedLine
            });
            series.push({
                fillColor: 'none',
                lineWidth: 1.5,
                lineColor: this.colors.titleColor,
                marker: {
                    enabled: false
                },
                data: actualLine
            });
            series.push({
                fillColor: 'none',
                lineWidth: 1.5,
                lineColor: this.colors.trendColor,
                marker: {
                    enabled: false
                },
                data: trendLine
            });

            const plannedAcres = (graphData.planned.length > 0) ? graphData.planned[graphData.planned.length - 1][1] : 0;
            const actualAcres = (graphData.actual.length > 0) ? graphData.actual[graphData.actual.length - 1][1] : 0;
            const trendAcres = (graphData.trend.length > 0) ? graphData.trend[graphData.trend.length - 1][1] : 0;
            const totalAcres = (plannedAcres + actualAcres + trendAcres);

            let yAxisMax = null;
            if (totalAcres === 0) yAxisMax = 10;

            this.showLoading(false);

            this.setState(prevState => ({
                ...prevState,
                chartOptions: {
                    ...prevState.chartOptions,
                    series: series,
                    yAxis: {
                        ...prevState.chartOptions.yAxis,
                        max: yAxisMax,
                        text: `Total ${this.props.settings.areaShortName}`,
                    },
                }

            }));
        } else {
            this.setState(prevState => ({
                ...prevState,
                chartOptions: {
                    ...prevState.chartOptions,
                    series: [],
                }
            }));
            this.showLoading(true);
        }
    }

    parseUTCTime(dateStr) {
        return moment.utc(dateStr).valueOf();
    }

    showLoading(isShow) {
        if (isShow) {
            this.setState(prevState => ({
                ...prevState,
                chartOptions: {
                    ...prevState.chartOptions,
                    title: {
                        text: '<span>Loading...</span>',
                        verticalAlign: 'middle',
                        layout: 'horizontal',
                        itemWidth: 800,
                        useHTML: Boolean,
                        style: { "color": "#fff", "fontSize": "1em", "text-align": "center" }
                    }
                }
            }));
        }
        else
            this.setState(prevState => ({
                ...prevState,
                chartOptions: {
                    ...prevState.chartOptions,
                    title: {
                        ...prevState.chartOptions.title,
                        text: '',
                    },
                }
            }));
    }

    render() {
        const { chartOptions } = this.state;
        return (<>
            <ReactHighcharts highcharts={Highcharts} ref={this.chartRef} options={chartOptions} />
        </>);
    }
}